<script setup lang="ts" generic="TSubject extends TSchoolSubjectListItem">
import type { TCompetenceDomainListItem, TSchoolSubjectGroupListItem, TSchoolSubjectListItem } from '@/interfaces';
import CustomAccordion from '@/core/elements/CustomAccordion.vue';
import CustomCheckbox from '@/core/elements/CustomCheckbox.vue';
import SchoolSubjectCheckboxList from '@/core/selects/SchoolSubjectCheckboxList.vue';
import { getSchoolSubjectGroupsWithSubjects } from '@/utils/schoolSubject.utils';
import { immutableSort, toRecord } from 'common/utils/array.utils';
import { ComponentPublicInstance, computed, onMounted, ref, shallowRef, toRefs, watch } from 'vue';

const emit = defineEmits<{
  'update:modelValue': [value: TSubject[]];
}>();

defineSlots<{
  // a row above the subjects, e.g. a choice that is not a subject; paddingClass aligns it with the subject rows
  prepend?(props: { paddingClass: string }): void;
}>();

const props = withDefaults(
  defineProps<{
    modelValue: TSubject[];
    availableSchoolSubjects: TSubject[];
    availableSchoolSubjectGroups: TSchoolSubjectGroupListItem[];
    availableCompetenceDomains: TCompetenceDomainListItem[];
    disabled?: boolean;
    readOnly?: boolean;
    showEmptyGroups?: boolean;
    singleSelect?: boolean;
    theme?: 'default' | 'noPadding' | 'schoolSubjectImport';
    canSelectGroups?: boolean;
  }>(),
  {
    theme: 'default',
    canSelectGroups: false,
  },
);
const { modelValue, availableSchoolSubjects, availableSchoolSubjectGroups, availableCompetenceDomains } = toRefs(props);

const accordionRefs = ref<(InstanceType<typeof CustomAccordion> | null)[]>([]);

const selectedSchoolSubjectsInternal = shallowRef<TSubject[]>([]);

const schoolSubjectGroups = computed(() => {
  if (props.showEmptyGroups) return immutableSort(availableSchoolSubjectGroups.value, (a, b) => a.order - b.order);
  return getSchoolSubjectGroupsWithSubjects(availableSchoolSubjectGroups.value, availableSchoolSubjects.value);
});

const schoolSubjectsByGroupId = computed(() => {
  const schoolSubjectsWithGroup = availableSchoolSubjects.value.filter(
    (schoolSubject): schoolSubject is TSubject & { groupId: number } => schoolSubject.groupId != null,
  );

  return toRecord(
    schoolSubjectsWithGroup,
    (schoolSubject) => schoolSubject.groupId,
    (schoolSubject, existing: TSubject[] = []) => [...existing, schoolSubject],
  );
});

const schoolSubjectsWithoutGroup = computed(() => {
  return availableSchoolSubjects.value.filter((subject) => !subject.groupId);
});

const ungroupedPaddingClass = computed<string>(() => (schoolSubjectGroups.value.length ? 'pl-xxl pr-md' : 'px-lg'));

const toggleSchoolSubjectGroupSelected = (group: TSchoolSubjectGroupListItem, value: boolean): void => {
  if (props.readOnly || props.disabled) return;
  const groupSubjects = schoolSubjectsByGroupId.value[group.id] ?? [];

  if (value) {
    const byId = new Map<number, TSubject>();
    for (const s of selectedSchoolSubjectsInternal.value) byId.set(s.id, s);
    for (const s of groupSubjects) byId.set(s.id, s);

    selectedSchoolSubjectsInternal.value = Array.from(byId.values());
  } else {
    const groupIds = new Set(groupSubjects.map((s) => s.id));
    selectedSchoolSubjectsInternal.value = selectedSchoolSubjectsInternal.value.filter((s) => !groupIds.has(s.id));
  }

  emit('update:modelValue', selectedSchoolSubjectsInternal.value);
};

const setAccordionRef = (el: Element | ComponentPublicInstance | null, index: number): void => {
  if (!el) return;
  accordionRefs.value[index] = el as InstanceType<typeof CustomAccordion>;
};

const selectedIds = computed(() => new Set(selectedSchoolSubjectsInternal.value.map((s) => s.id)));

const getGroupSelectionStatus = (groupId: number): boolean | 'intermediate' => {
  const groupSubjects = schoolSubjectsByGroupId.value[groupId] ?? [];
  if (groupSubjects.length === 0) return false;

  const selectedCount = groupSubjects.reduce((acc, s) => acc + (selectedIds.value.has(s.id) ? 1 : 0), 0);

  if (selectedCount === 0) return false;
  if (selectedCount === groupSubjects.length) return true;
  return 'intermediate';
};

onMounted(() => {
  accordionRefs.value[0]?.show();
});

watch(
  modelValue,
  () => {
    selectedSchoolSubjectsInternal.value = modelValue.value;
  },
  { immediate: true },
);
</script>

<template>
  <div :class="['flex flex-col', theme === 'default' && 'p-sm']">
    <slot name="prepend" :padding-class="ungroupedPaddingClass" />
    <SchoolSubjectCheckboxList
      v-if="schoolSubjectsWithoutGroup.length"
      :class="[ungroupedPaddingClass, schoolSubjectGroups.length && 'pb-sm', 'pt-md']"
      :model-value="selectedSchoolSubjectsInternal"
      :available-competence-domains="availableCompetenceDomains"
      :available-school-subjects="schoolSubjectsWithoutGroup"
      :disabled="disabled"
      :read-only="readOnly"
      :single-select="singleSelect"
      :theme="theme"
      @update:model-value="emit('update:modelValue', $event)"
    />
    <CustomAccordion
      v-for="(group, index) in schoolSubjectGroups"
      :key="group.id"
      :ref="(el) => setAccordionRef(el, index)"
      :identifier="`school-subject-group-checkbox-list-${group.id}`"
      :button-style="'!rounded-group' + (theme === 'schoolSubjectImport' ? ' py-lg' : ' pb-sm pt-md')"
      :accordion-collapse-body-style="theme === 'schoolSubjectImport' ? 'pb-lg pt-0 !rounded-b-group' : 'py-sm'"
    >
      <template #header>
        <div class="flex items-center gap-sm">
          <CustomCheckbox
            v-if="!disabled && !readOnly && canSelectGroups"
            :model-value="getGroupSelectionStatus(group.id)"
            :label="group.name"
            :value="group.id"
            @update:model-value="toggleSchoolSubjectGroupSelected(group, $event)"
          />
          <span class="text-left text-body font-semibold">{{ group.name }}</span>
        </div>
      </template>
      <template #items>
        <SchoolSubjectCheckboxList
          class="pl-xxl pr-md"
          :model-value="selectedSchoolSubjectsInternal"
          :available-competence-domains="availableCompetenceDomains"
          :available-school-subjects="schoolSubjectsByGroupId[group.id] ?? []"
          :disabled="disabled"
          :read-only="readOnly"
          :single-select="singleSelect"
          :theme="theme"
          @update:model-value="emit('update:modelValue', $event)"
        />
      </template>
    </CustomAccordion>
  </div>
</template>
